import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import { TbTriangleInvertedFilled } from "react-icons/tb";
import DemoLoader from "../../component/Loader/demoLoader";
import startRecordingIcon from "../../Asset/startRecordingIcon.svg";
import restartIcon from "../../Asset/Restart.svg";
import pauseIcon from "../../Asset/Pause.svg";
import startIcon from "../../Asset/Start.svg";
import cancelIcon from "../../Asset/Cancel.svg";
import stopIcon from "../../Asset/Stop.svg";
import reuploadIcon from "../../Asset/reupload.svg";

const RecordingTray = ({
  loading,
  finalizing,
  recording,
  controlTrayOpen,
  retryOnlyMode,
  processingHaltedMode,
  instantTrayCollapse,
  recordingPaused,
  elapsedTime,
  voiceLevel,
  onMicClick,
  onStop,
  onRestart,
  onPauseResume,
  onCancel,
  onRetry,
  onRetryCancel,
  onProcessingCancel,
  onProcessingHaltedCancel,
}) => {
  const STROKE_MIN_PX = 2.5;
  const STROKE_MAX_PX = 6;
  const RMS_REF = 4500;
  const DECAY_IDLE_AFTER_MS = 300;
  const TICK_MS = 50;
  const SMOOTH_ALPHA = 0.3;

  const trayState = useMemo(() => {
    if (retryOnlyMode || processingHaltedMode) return "FAILED";
    if (recording && controlTrayOpen) return "EXPANDED";
    if (recording) return "COMPACT";
    return "IDLE";
  }, [controlTrayOpen, processingHaltedMode, recording, retryOnlyMode]);

  const [traySettled, setTraySettled] = useState(false);
  const [ringStrokePx, setRingStrokePx] = useState(0);
  const trayOpen = Boolean(controlTrayOpen || retryOnlyMode || processingHaltedMode);
  const lastVoiceAtRef = useRef(0);
  const currentStrokeRef = useRef(0);
  const wasRingActiveRef = useRef(false);

  useEffect(() => {
    if (!trayOpen) {
      setTraySettled(false);
      return;
    }
    const delay = instantTrayCollapse || retryOnlyMode || processingHaltedMode ? 0 : 150;
    const id = setTimeout(() => setTraySettled(true), delay);
    return () => clearTimeout(id);
  }, [trayOpen, instantTrayCollapse, retryOnlyMode, processingHaltedMode]);

  const rmsRaw = Math.max(0, Number.isFinite(voiceLevel) ? voiceLevel : 0);
  const levelNorm = Math.max(0, Math.min(1, rmsRaw / RMS_REF));
  const inExpanded = trayState === "EXPANDED";
  const ringActive = inExpanded && traySettled;

  useEffect(() => {
    if (ringActive && !wasRingActiveRef.current) {
      // Show border immediately on expand; then keep updating from live RMS.
      lastVoiceAtRef.current = Date.now();
      currentStrokeRef.current = STROKE_MIN_PX;
      setRingStrokePx(STROKE_MIN_PX);
    }

    wasRingActiveRef.current = ringActive;

    if (ringActive && rmsRaw > 1) {
      lastVoiceAtRef.current = Date.now();
    }
  }, [rmsRaw, ringActive]);

  useEffect(() => {
    if (!ringActive) {
      currentStrokeRef.current = 0;
      setRingStrokePx(0);
      return undefined;
    }

    const tick = () => {
      const now = Date.now();
      const recentlyActive = now - lastVoiceAtRef.current < DECAY_IDLE_AFTER_MS;
      const targetStroke = recentlyActive
        ? STROKE_MIN_PX + (STROKE_MAX_PX - STROKE_MIN_PX) * levelNorm
        : 0;

      const nextStroke =
        currentStrokeRef.current + (targetStroke - currentStrokeRef.current) * SMOOTH_ALPHA;
      currentStrokeRef.current = Math.max(0, nextStroke);
      setRingStrokePx(currentStrokeRef.current);
    };

    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [levelNorm, ringActive]);

  const handleCancelPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onCancel?.();
  };
  const handleStopPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onStop?.();
  };
  const handleRestartPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRestart?.();
  };
  const handlePauseResumePointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onPauseResume?.();
  };
  const handleMicPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onMicClick?.();
  };
  const getItemRevealClass = () =>
    `transition-all duration-200 ${
      traySettled ? "opacity-100 translate-x-0" : "opacity-0 translate-x-1"
    }`;
  const getItemRevealStyle = (orderFromRight) => ({
    transitionDelay: traySettled ? `${orderFromRight * 55}ms` : "0ms",
  });

  return (
    <div className="shrink-0 flex justify-center items-center py-3 min-h-[76px]">
      {loading ? (
        <div className="relative w-[52px] h-[52px] flex items-center justify-center">
          <div className="scale-100 origin-center pointer-events-none">
            <DemoLoader />
          </div>
        </div>
      ) : finalizing ? (
        <div className="relative w-[52px] h-[52px] flex items-center justify-center">
          <div className="relative group w-full h-full flex items-center justify-center">
            <div className="scale-100 origin-center pointer-events-none">
              <DemoLoader />
            </div>
            <button
              type="button"
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onProcessingCancel?.();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              className="absolute inset-0 z-10 rounded-full"
              aria-label="Halt processing"
            />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
              Halt Processing
              <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
            </span>
          </div>
        </div>
      ) : (
        <div className="relative flex items-center h-[52px]">
          <div className="relative group">
            <button
              type="button"
              onClick={onMicClick}
              className={`relative z-10 w-[49px] h-[49px] rounded-full flex items-center justify-center transition-opacity duration-150 ${
                ((recording && controlTrayOpen && traySettled) || retryOnlyMode || processingHaltedMode)
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
              }`}
              aria-label={recording ? "Toggle recording controls" : "Start recording"}
            >
              <ReactSVG
                src={startRecordingIcon}
                beforeInjection={(svg) => svg.setAttribute("class", "w-full h-full")}
                className="w-[49px] h-[49px]"
              />
            </button>
            {!recording && (
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                Tap to Start Recording
                <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
              </span>
            )}
          </div>

          <div
            className={`absolute left-1/2 -translate-x-1/2 h-[52px] rounded-full transform-gpu transition-[max-width,opacity,padding] ${
              instantTrayCollapse ? "duration-0" : "duration-500"
            } ease-in-out ${
              trayOpen
                ? `max-w-[360px] opacity-100 ${
                    traySettled ? "overflow-visible pointer-events-auto" : "overflow-hidden pointer-events-none"
                  }`
                : "max-w-0 opacity-0 pointer-events-none overflow-hidden"
            }`}
            style={{
              background: traySettled
                ? "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)"
                : "transparent",
              padding: traySettled ? `${ringStrokePx.toFixed(2)}px` : 0,
              filter: "none",
            }}
          >
            <div
              className={`h-full rounded-full bg-[#1B1F24] border border-[#2C323A] px-2 flex items-center gap-1.5 transition-opacity duration-150 ${
                traySettled ? "opacity-100" : "opacity-0"
              }`}
            >
              {retryOnlyMode || processingHaltedMode ? (
                <>
                  {retryOnlyMode && (
                    <div
                      className={`relative group ${getItemRevealClass()}`}
                      style={getItemRevealStyle(2)}
                    >
                      <button
                        type="button"
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onRetry?.();
                        }}
                        onClick={(event) => event.preventDefault()}
                        className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                        aria-label="Retry upload"
                      >
                        <ReactSVG
                          src={reuploadIcon}
                          beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                          className="w-4 h-4"
                        />
                      </button>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                        Retry
                        <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                      </span>
                    </div>
                  )}

                  <div
                    className={`relative group ${getItemRevealClass()}`}
                    style={getItemRevealStyle(retryOnlyMode ? 1 : 1)}
                  >
                    <button
                      type="button"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (processingHaltedMode) onProcessingHaltedCancel?.();
                        else onRetryCancel?.();
                      }}
                      onClick={(event) => event.preventDefault()}
                      className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                      aria-label="Cancel"
                    >
                      <ReactSVG
                        src={cancelIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                        className="w-4 h-4"
                      />
                    </button>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                      Cancel
                      <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                    </span>
                  </div>

                  <div
                    className={`relative group ${getItemRevealClass()}`}
                    style={getItemRevealStyle(0)}
                  >
                    <button
                      type="button"
                      onPointerDown={handleMicPointerDown}
                      onClick={(event) => event.preventDefault()}
                      className="relative z-20 h-10 w-10 rounded-full bg-transparent hover:bg-transparent transition flex items-center justify-center"
                      aria-label="Start new recording"
                    >
                      <ReactSVG
                        src={startRecordingIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-10 h-10")}
                        className="w-10 h-10"
                      />
                    </button>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                      New Recording
                      <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                    </span>
                  </div>
                </>
              ) : (
                <>
              <div
                className={`relative group ${getItemRevealClass()}`}
                style={getItemRevealStyle(4)}
              >
                <button
                  type="button"
                  onPointerDown={handleStopPointerDown}
                  onClick={(event) => event.preventDefault()}
                  className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                  aria-label="Stop recording"
                >
                  <ReactSVG
                    src={stopIcon}
                    beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                    className="w-4 h-4"
                  />
                </button>
                {traySettled && (
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                    Stop
                    <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                  </span>
                )}
              </div>

              <div
                className={`relative group ${getItemRevealClass()}`}
                style={getItemRevealStyle(3)}
              >
                <button
                  type="button"
                  onPointerDown={handleRestartPointerDown}
                  onClick={(event) => event.preventDefault()}
                  className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                  aria-label="Restart recording"
                >
                  <ReactSVG
                    src={restartIcon}
                    beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                    className="w-4 h-4"
                  />
                </button>
                {traySettled && (
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                    Restart
                    <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                  </span>
                )}
              </div>

              <span
                className={`text-white text-sm font-inter min-w-[46px] text-center ${getItemRevealClass()}`}
                style={getItemRevealStyle(2)}
              >
                {elapsedTime}
              </span>

              <div
                className={`relative group ${getItemRevealClass()}`}
                style={getItemRevealStyle(1)}
              >
                <button
                  type="button"
                  onPointerDown={handlePauseResumePointerDown}
                  onClick={(event) => event.preventDefault()}
                  className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                  aria-label={recordingPaused ? "Resume recording" : "Pause recording"}
                >
                  {recordingPaused ? (
                    <ReactSVG
                      src={startIcon}
                      beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                      className="w-4 h-4"
                    />
                  ) : (
                    <ReactSVG
                      src={pauseIcon}
                      beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                      className="w-4 h-4"
                    />
                  )}
                </button>
                {traySettled && (
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                    {recordingPaused ? "Resume" : "Pause"}
                    <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                  </span>
                )}
              </div>

              <div
                className={`relative group ${getItemRevealClass()}`}
                style={getItemRevealStyle(0)}
              >
                <button
                  type="button"
                  onPointerDown={handleCancelPointerDown}
                  onClick={(event) => event.preventDefault()}
                  className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                  aria-label="Cancel recording"
                >
                  <ReactSVG
                    src={cancelIcon}
                    beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                    className="w-4 h-4"
                  />
                </button>
                {traySettled && (
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                    Cancel
                    <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                  </span>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordingTray;
